import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../stores';
import './Admin.css';
export function AIProviders() {
    const { accessToken } = useAuthStore();
    const [providers, setProviders] = useState([]);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const fetchData = useCallback(async () => {
        try {
            const [providersRes, modulesRes] = await Promise.all([
                fetch('/api/admin/ai-providers', {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }),
                fetch('/api/admin/ai-provider-modules', {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }),
            ]);
            if (!providersRes.ok)
                throw new Error('Failed to fetch AI providers');
            if (!modulesRes.ok)
                throw new Error('Failed to fetch AI provider modules');
            setProviders(await providersRes.json());
            setModules(await modulesRes.json());
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load AI providers');
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const rows = useMemo(() => {
        const configured = providers.map((p) => ({
            ...p,
            listKey: `cfg-${p.id}`,
            isCatalogOnly: false,
        }));
        const configuredTypes = new Set(providers.map((p) => p.type.toLowerCase()));
        const catalogExtras = modules
            .filter((m) => !configuredTypes.has(m.type.toLowerCase()))
            .map((m) => ({
            id: 0,
            name: m.label,
            type: m.type,
            apiKey: '',
            isActive: false,
            description: m.hasModule
                ? `Modül hazır (${m.moduleFileName}); henüz yapılandırılmamış.`
                : 'LLM modül dosyası yok; henüz implement edilmedi.',
            hasModule: m.hasModule,
            moduleFileName: m.moduleFileName,
            moduleClassName: m.moduleClassName,
            isCatalogOnly: true,
            listKey: `cat-${m.type}`,
        }));
        return [...configured, ...catalogExtras];
    }, [providers, modules]);
    const providerHasApiKey = (provider) => provider.hasApiKey === true || (Boolean(provider.apiKey) && !/^•+$/.test(provider.apiKey));
    const handleToggleActive = async (provider) => {
        if (provider.isCatalogOnly || !provider.id)
            return;
        setActionLoading(provider.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/ai-providers/${provider.id}/toggle`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to toggle provider status');
            }
            await fetchData();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to toggle provider status');
        }
        finally {
            setActionLoading(null);
        }
    };
    const getProviderTypeLabel = (type) => {
        return modules.find((m) => m.type === type)?.label || type;
    };
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "AI Providers" }), _jsx("p", { children: "AI sa\u011Flay\u0131c\u0131lar\u0131n\u0131 y\u00F6netin; mod\u00FCl dosyas\u0131 olmayan tipler de listede g\u00F6r\u00FCn\u00FCr" })] }), _jsx(Link, { className: "admin-btn admin-btn-primary", to: "/admin/ai-providers/new", children: "+ Yeni Provider Ekle" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["AI Providers (", rows.length, ")"] }) }), rows.length === 0 ? (_jsxs("div", { className: "admin-empty-state", children: [_jsx("p", { children: "Hen\u00FCz AI provider tan\u0131mlanmam\u0131\u015F." }), _jsx(Link, { className: "admin-btn admin-btn-primary", to: "/admin/ai-providers/new", children: "\u0130lk Provider'\u0131 Ekle" })] })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Ad" }), _jsx("th", { children: "Tip" }), _jsx("th", { children: "Mod\u00FCl" }), _jsx("th", { children: "Base URL" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "\u0130\u015Flemler" })] }) }), _jsx("tbody", { children: rows.map((provider) => (_jsxs("tr", { className: provider.isCatalogOnly ? 'admin-row-muted' : undefined, children: [_jsx("td", { children: _jsxs("div", { className: "provider-info", children: [_jsxs("div", { className: "provider-name-row", children: [_jsx("span", { className: "provider-name", children: provider.name }), provider.isCatalogOnly && (_jsx("span", { className: "provider-catalog-badge", title: "Katalog kayd\u0131", children: "Katalog" })), !provider.isCatalogOnly && !providerHasApiKey(provider) && (_jsxs("span", { className: "provider-missing-api-key-badge", title: "API Key tan\u0131ml\u0131 de\u011Fil", children: [_jsx(ExclamationTriangleIcon, { "aria-hidden": "true" }), "API Key yok"] }))] }), provider.description && (_jsx("span", { className: "provider-description", children: provider.description }))] }) }), _jsx("td", { children: _jsx("span", { className: "provider-type-badge", children: getProviderTypeLabel(provider.type) }) }), _jsx("td", { children: provider.hasModule ? (_jsx("span", { className: "provider-module-badge provider-module-badge-ok", title: provider.moduleFileName || undefined, children: provider.moduleFileName || 'Var' })) : (_jsx("span", { className: "provider-module-badge provider-module-badge-missing", children: "Dosya yok" })) }), _jsx("td", { children: _jsx("span", { className: "provider-base-url", children: provider.isCatalogOnly ? '—' : provider.baseUrl || '-' }) }), _jsx("td", { children: provider.isCatalogOnly ? (_jsx("span", { className: "admin-muted-text", children: "Yap\u0131land\u0131r\u0131lmam\u0131\u015F" })) : (_jsx("button", { className: `admin-toggle-btn ${provider.isActive ? 'active' : ''}`, onClick: () => handleToggleActive(provider), disabled: actionLoading === provider.id, title: provider.isActive ? 'Deaktif Et' : 'Aktif Et', children: provider.isActive ? '✓ Aktif' : '✗ Pasif' })) }), _jsx("td", { children: _jsx("div", { className: "admin-action-buttons", children: provider.isCatalogOnly ? (_jsx(Link, { className: "admin-btn admin-btn-secondary admin-btn-sm", to: `/admin/ai-providers/new?type=${encodeURIComponent(provider.type)}`, children: "Yap\u0131land\u0131r" })) : (_jsx(Link, { className: "admin-edit-btn", to: `/admin/ai-providers/${provider.id}`, title: "Detay", children: "\u270F\uFE0F" })) }) })] }, provider.listKey))) })] }) }))] })] }));
}
